/**
 * 统一的错误消息提取工具
 *
 * 从 unknown 类型的 catch 错误中安全提取可读消息，
 * 兼容 Axios 响应、标准 Error、字符串等多种形式。
 */

interface AxiosLikeError {
  response?: { data?: { detail?: unknown; message?: unknown } }
  message?: string
}

function isAxiosLikeError(val: unknown): val is AxiosLikeError {
  return typeof val === 'object' && val !== null && 'response' in val
}

/** pydantic v2 自定义校验器抛出的消息带 "Value error, " 前缀，对用户无意义 */
const PYDANTIC_VALUE_ERROR_PREFIX = /^Value error,\s*/

function readMessage(value: unknown): string {
  if (typeof value === 'string' && value.trim()) {
    return value
  }
  // FastAPI 422 的 detail 是 [{ loc, msg, type }] 数组，取第一条可读的 msg
  if (Array.isArray(value)) {
    for (const entry of value) {
      const msg = readMessage((entry as Record<string, unknown> | null)?.msg)
      if (msg) return msg.replace(PYDANTIC_VALUE_ERROR_PREFIX, '')
    }
    return ''
  }
  if (value && typeof value === 'object') {
    const message = (value as Record<string, unknown>).message
    if (typeof message === 'string' && message.trim()) {
      return message
    }
  }
  return ''
}

/** 从 unknown 类型错误中提取可读消息 */
export function getErrorMessage(error: unknown, fallback = '操作失败'): string {
  if (typeof error === 'string') return error

  if (isAxiosLikeError(error)) {
    const detail = readMessage(error.response?.data?.detail)
    if (detail) return detail
    const message = readMessage(error.response?.data?.message)
    if (message) return message
  }

  if (error instanceof Error) return error.message

  return fallback
}
